import { IssueTypeEnum } from '../issue-source';
import { IssueStatusEnum, IssueStatusType } from '../issue-status.enum';
import { ROLE, Role } from '../role';
import type {
  ActionWorkflowField,
  AuditIssueFieldValue,
  BaseIssueFieldValue,
  BasicIssueField,
  ControlDeficiencyIssueFieldValue,
  IssueFieldValue,
  SLAField,
} from './field-types';

const reporterDraftBaseFields: readonly (
  | BasicIssueField
  | ActionWorkflowField
)[] = [
  'title',
  'owner',
  'severity',
  'dueDate',
  'description',
  'impact',
  'attachments',
  'govmapGroups',
  'executionType',
  'actions',
] as const;

const reporterDraftControlDeficiencyFields: readonly ControlDeficiencyIssueFieldValue[] =
  [...reporterDraftBaseFields, 'classification', 'rootCause'] as const;

const reporterDraftAuditFields: readonly AuditIssueFieldValue[] = [
  ...reporterDraftBaseFields,
  'classification',
  'rootCause',
  'recommendations',
] as const;

const typeManagerEditableBaseFields: readonly (
  | BasicIssueField
  | ActionWorkflowField
  | SLAField
)[] = [
  'severity',
  'dueDate',
  'govmapGroups',
  'impact',
  'owner',
  'approver',
  'attachments',
  'watchers',
  'executionType',
  'actions',
  'slaEnabled',
] as const;

const typeManagerEditableAuditFields: readonly AuditIssueFieldValue[] = [
  ...typeManagerEditableBaseFields,
  'classification',
  'includeInAuditReport',
  'managementResponse',
  'recommendations',
  'resolution',
  'rootCause',
] as const;

const typeManagerEditableControlDeficiencyFields: readonly ControlDeficiencyIssueFieldValue[] =
  [
    ...typeManagerEditableBaseFields,
    'classification',
    'managementResponse',
    'rootCause',
    'resolution',
    'remediation',
  ] as const;

const ownerEditableFields: readonly BaseIssueFieldValue[] = [
  'dueDate',
  'attachments',
  'executionType',
  'actions',
] as const;

const typeManagerToBeApprovedBaseFields: readonly (
  | BasicIssueField
  | ActionWorkflowField
  | SLAField
)[] = [
  'severity',
  'dueDate',
  'govmapGroups',
  'impact',
  'owner',
  'approver',
  'attachments',
  'watchers',
  'executionType',
  'slaEnabled',
] as const;

const typeManagerToBeApprovedAuditFields: readonly AuditIssueFieldValue[] = [
  ...typeManagerToBeApprovedBaseFields,
  'classification',
  'includeInAuditReport',
  'managementResponse',
  'recommendations',
  'resolution',
  'rootCause',
] as const;

const typeManagerToBeApprovedControlDeficiencyFields: readonly ControlDeficiencyIssueFieldValue[] =
  [
    ...typeManagerToBeApprovedBaseFields,
    'classification',
    'managementResponse',
    'rootCause',
    'resolution',
    'remediation',
  ] as const;

const ownerToBeApprovedFields: readonly BaseIssueFieldValue[] = [
  'dueDate',
  'attachments',
  'executionType',
] as const;

type IssueEditableFieldsByType = {
  [IssueTypeEnum.ISSUE]: Partial<
    Record<
      IssueStatusType,
      readonly { roles: readonly Role[]; fields: readonly IssueFieldValue[] }[]
    >
  >;
  [IssueTypeEnum.EXCEPTION]: Partial<
    Record<
      IssueStatusType,
      readonly { roles: readonly Role[]; fields: readonly IssueFieldValue[] }[]
    >
  >;
  [IssueTypeEnum.INCIDENT]: Partial<
    Record<
      IssueStatusType,
      readonly { roles: readonly Role[]; fields: readonly IssueFieldValue[] }[]
    >
  >;
  [IssueTypeEnum.AUDIT_FINDING]: Partial<
    Record<
      IssueStatusType,
      readonly {
        roles: readonly Role[];
        fields: readonly AuditIssueFieldValue[];
      }[]
    >
  >;
  [IssueTypeEnum.CONTROL_DEFICIENCY]: Partial<
    Record<
      IssueStatusType,
      readonly {
        roles: readonly Role[];
        fields: readonly ControlDeficiencyIssueFieldValue[];
      }[]
    >
  >;
};

const basicIssueTypeEditableConfig = {
  [IssueStatusEnum.DRAFT]: [
    {
      roles: [ROLE.CREATOR],
      fields: reporterDraftBaseFields,
    },
    {
      roles: [ROLE.ISSUE_TYPE_MANAGER],
      fields: typeManagerEditableBaseFields,
    },
  ],
  [IssueStatusEnum.IN_REVIEW]: [
    {
      roles: [ROLE.ISSUE_TYPE_MANAGER],
      fields: typeManagerEditableBaseFields,
    },
  ],
  [IssueStatusEnum.IN_PROGRESS]: [
    {
      roles: [ROLE.ISSUE_TYPE_MANAGER],
      fields: typeManagerEditableBaseFields,
    },
    {
      roles: [ROLE.OWNER],
      fields: ownerEditableFields,
    },
  ],
  [IssueStatusEnum.TO_BE_APPROVED]: [
    {
      roles: [ROLE.ISSUE_TYPE_MANAGER],
      fields: typeManagerToBeApprovedBaseFields,
    },
    {
      roles: [ROLE.OWNER],
      fields: ownerToBeApprovedFields,
    },
  ],
  [IssueStatusEnum.CLOSED]: [],
  [IssueStatusEnum.DISCARDED]: [],
} as const satisfies Partial<
  Record<
    IssueStatusType,
    readonly { roles: readonly Role[]; fields: readonly IssueFieldValue[] }[]
  >
>;

export const ISSUE_EDITABLE_FIELDS: IssueEditableFieldsByType = {
  [IssueTypeEnum.ISSUE]: basicIssueTypeEditableConfig,
  [IssueTypeEnum.EXCEPTION]: basicIssueTypeEditableConfig,
  [IssueTypeEnum.INCIDENT]: basicIssueTypeEditableConfig,
  [IssueTypeEnum.AUDIT_FINDING]: {
    [IssueStatusEnum.DRAFT]: [
      {
        roles: [ROLE.CREATOR],
        fields: reporterDraftAuditFields,
      },
      {
        roles: [ROLE.ISSUE_TYPE_MANAGER],
        fields: typeManagerEditableAuditFields,
      },
    ],
    [IssueStatusEnum.IN_REVIEW]: [
      {
        roles: [ROLE.ISSUE_TYPE_MANAGER],
        fields: typeManagerEditableAuditFields,
      },
    ],
    [IssueStatusEnum.TO_BE_PUBLISHED]: [
      {
        roles: [ROLE.ISSUE_TYPE_MANAGER],
        fields: typeManagerEditableAuditFields,
      },
    ],
    [IssueStatusEnum.PENDING_ACCEPTANCE]: [
      {
        roles: [ROLE.ISSUE_TYPE_MANAGER],
        fields: typeManagerEditableAuditFields,
      },
      {
        roles: [ROLE.OWNER],
        fields: ownerEditableFields,
      },
    ],
    [IssueStatusEnum.IN_PROGRESS]: [
      {
        roles: [ROLE.ISSUE_TYPE_MANAGER],
        fields: typeManagerEditableAuditFields,
      },
      {
        roles: [ROLE.OWNER],
        fields: ownerEditableFields,
      },
    ],
    [IssueStatusEnum.TO_BE_APPROVED]: [
      {
        roles: [ROLE.ISSUE_TYPE_MANAGER],
        fields: typeManagerToBeApprovedAuditFields,
      },
      {
        roles: [ROLE.OWNER],
        fields: ownerToBeApprovedFields,
      },
    ],
    [IssueStatusEnum.CLOSED]: [],
    [IssueStatusEnum.DISCARDED]: [],
  },
  [IssueTypeEnum.CONTROL_DEFICIENCY]: {
    [IssueStatusEnum.DRAFT]: [
      {
        roles: [ROLE.CREATOR],
        fields: reporterDraftControlDeficiencyFields,
      },
      {
        roles: [ROLE.ISSUE_TYPE_MANAGER],
        fields: typeManagerEditableControlDeficiencyFields,
      },
    ],
    [IssueStatusEnum.IN_REVIEW]: [
      {
        roles: [ROLE.ISSUE_TYPE_MANAGER],
        fields: typeManagerEditableControlDeficiencyFields,
      },
    ],
    [IssueStatusEnum.IN_PROGRESS]: [
      {
        roles: [ROLE.ISSUE_TYPE_MANAGER],
        fields: typeManagerEditableControlDeficiencyFields,
      },
      {
        roles: [ROLE.OWNER],
        fields: ownerEditableFields,
      },
    ],
    [IssueStatusEnum.TO_BE_APPROVED]: [
      {
        roles: [ROLE.ISSUE_TYPE_MANAGER],
        fields: typeManagerToBeApprovedControlDeficiencyFields,
      },
      {
        roles: [ROLE.OWNER],
        fields: ownerToBeApprovedFields,
      },
    ],
    [IssueStatusEnum.CLOSED]: [],
    [IssueStatusEnum.DISCARDED]: [],
  },
} as const;
